export const suppliersArticles = [
    {
        id: 'suppliers.overview',
        title: 'Suppliers Overview',
        category: 'Suppliers',
        order: 1,
        summary: 'Managing your supplier database and relationships',
        content: `
The Suppliers page is your central hub for managing supplier information, pricing, and compliance documentation.

**Supplier Information:**

- Company details and contacts
- Banking information for payments
- Insurance and compliance records
- Supplier-specific pricing
- Order and payment history

**Key Features:**

- Add and manage suppliers
- Send onboarding questionnaires to new suppliers and subcontractors
- Track supplier compliance
- Maintain supplier pricing
- View supplier orders and payments

**Searching:** the search box matches supplier code, company name, contact name and email —
type any words in any order (e.g. a contact's first name, or part of an email address).
    `,
        expandableSections: [
            {
                title: 'Supplier Grid',
                content: `
The main grid shows all suppliers with:

- **Supplier Code** - Unique identifier
- **Company Name** - Trading name
- **Contact** - Primary contact
- **Phone/Email** - Contact details
- **Status** - Active/Inactive
- **Compliance** - Compliance status indicator

Use filters to find specific suppliers or groups.
        `,
            },
            {
                title: 'Supplier Groups',
                content: `
Organize suppliers into groups for:

- Filtering and reporting
- Bulk communications
- Trade categorization

Examples: Electricians, Plumbers, Concreters, Material Suppliers
        `,
            },
        ],
        keywords: ['suppliers', 'vendors', 'subcontractors', 'trades', 'search suppliers'],
        route: '/suppliers',
        relatedArticles: ['suppliers.add', 'suppliers.prices', 'suppliers.insurance', 'suppliers.onboarding'],
        screenshots: [
            {
                src: 'suppliers/suppliers-overview.png',
                alt: 'Suppliers page overview',
                caption: 'The Suppliers page displays all your vendors and subcontractors',
            },
        ],
    },
    {
        id: 'suppliers.add',
        title: 'Adding Suppliers',
        category: 'Suppliers',
        order: 2,
        summary: 'How to create new supplier records',
        content: `
Create supplier records to maintain your list of approved vendors and subcontractors.

**Required Information:**

- Company Name
- Supplier Code (or auto-generate)

**Recommended Information:**

- ABN (Australian Business Number)
- Contact details
- Payment terms
- Banking details
    `,
        expandableSections: [
            {
                title: 'Creating a Supplier',
                content: `
1. Click **Add Supplier** in the toolbar
2. Enter required details:
   - Company Name
   - Supplier Code
3. Add contact information:
   - Primary contact name
   - Phone, email, address
4. Set up banking (optional):
   - BSB and Account Number
   - Account Name
5. Add to supplier groups (optional)
6. Click **Save**

**GST.** Tick **GST Registered** when the supplier charges GST (the ABN lookup fills this from the Australian Business Register and records registration dates). For a registered supplier whose supplies are nevertheless GST-free — superannuation funds, some insurers, government charges — tick **Supplies are GST-free by default**: new invoices from them start with Tax Code GST Free, and the ledger and Xero / QuickBooks treat them as GST-free. Either flag can be overridden on an individual invoice.
        `,
            },
            {
                title: 'Supplier Code Conventions',
                content: `
Supplier codes help identify suppliers:

- **Trade prefix** - ELEC (electrical), PLMB (plumbing)
- **Sequential number** - ELEC-001, ELEC-002
- **Name-based** - SMITH-ELEC, ABC-PLUMB

Keep codes:
- Unique
- Memorable
- Consistent in format
        `,
            },
        ],
        keywords: ['add supplier', 'new supplier', 'create supplier'],
        route: '/suppliers',
        relatedArticles: ['suppliers.overview', 'suppliers.banking'],
        screenshots: [
            {
                src: 'suppliers/suppliers-add.png',
                alt: 'Add Supplier form',
                caption: 'Create new supplier records with contact and banking details',
            },
        ],
    },
    {
        id: 'suppliers.banking',
        title: 'Supplier Banking Details',
        category: 'Suppliers',
        order: 3,
        summary: 'Managing supplier bank details for payments',
        content: `
Banking details are required for generating payment files (ABA) and processing electronic payments.

**Required for payments:**

- BSB Number (6 digits)
- Account Number
- Account Name

**Who can see and change them:** only owners, admins and managers see and edit the BSB and account number. Everyone else sees that bank details are on file and the last four digits. Every change of BSB or account number is kept on the supplier's record (who, when, from → to, last four digits only) and every owner and admin gets an in-app notice, so a quiet swap before a payment run cannot go unnoticed. Details entered through the onboarding questionnaire arrive unverified until someone ticks **Verify bank details** after a call-back.
    `,
        expandableSections: [
            {
                title: 'Adding Banking Details',
                content: `
1. Open the supplier record
2. Go to the **Banking** tab
3. Enter:
   - BSB (6-digit bank-state-branch)
   - Account Number
   - Account Name (as registered)
4. Save the supplier

**Verification:**
- BSB format is validated automatically
- Account name should match exactly for ABA files
        `,
            },
            {
                title: 'ABA File Requirements',
                content: `
For ABA direct deposit files:

- Account Name: Max 32 characters
- Account Number: 6-9 digits typically
- BSB: Must be valid Australian BSB

Verify details with the supplier to avoid payment errors.
        `,
            },
        ],
        keywords: ['banking', 'bank details', 'BSB', 'account', 'payments'],
        route: '/suppliers',
        relatedArticles: ['suppliers.overview', 'accounting.payments'],
    },
    {
        id: 'suppliers.insurance',
        title: 'Supplier Insurance',
        category: 'Suppliers',
        order: 4,
        summary: 'Tracking supplier insurance and compliance',
        content: `
Track insurance policies and expiry dates to ensure supplier compliance before placing orders.

**Insurance Types:**

- Workers Compensation
- Public Liability
- Professional Indemnity
- Contractors Licence
- Builders Licence
    `,
        expandableSections: [
            {
                title: 'Adding Insurance Records',
                content: `
1. Open the supplier record
2. Go to the **Insurance** tab
3. Click **Add Insurance**
4. Enter:
   - Insurance Type
   - Policy Number
   - Insurer Name
   - Expiry Date
   - Coverage Amount (optional)
5. Optionally upload policy document
6. Save
        `,
            },
            {
                title: 'Compliance Alerts',
                content: `
The system tracks insurance expiry:

- **Green** - Valid insurance
- **Yellow** - Expiring within 30 days
- **Red** - Expired insurance

Configure email alerts in Settings to notify when insurances are expiring.
        `,
            },
        ],
        keywords: ['insurance', 'compliance', 'public liability', 'workers comp', 'licence'],
        route: '/suppliers',
        relatedArticles: ['suppliers.overview', 'suppliers.add'],
    },
    {
        id: 'suppliers.prices',
        title: 'Supplier Pricing',
        category: 'Suppliers',
        order: 5,
        summary: 'Managing supplier-specific prices for catalogue items',
        content: `
Supplier pricing allows you to maintain negotiated rates that override the standard catalogue prices when that supplier is assigned.

**Price Hierarchy:**

1. Supplier Price (if assigned and exists)
2. Catalogue Price (selected price level)
    `,
        expandableSections: [
            {
                title: 'Setting Supplier Prices',
                content: `
1. Open the supplier record
2. Go to the **Prices** tab
3. Click **Add Price** or search for items
4. Enter:
   - Catalogue Item (search by code)
   - Supplier Price
   - Effective Date
5. Save

**Bulk Import:**
1. Click **Import Prices**
2. Upload Excel/CSV with:
   - Price Code
   - Supplier Price
3. Map columns and import
        `,
            },
            {
                title: 'Price Application',
                content: `
Supplier prices are a **history**: every award, import or manual entry adds a dated row. Everywhere in the app — the BOQ, the nominated-supplier picker, estimate review, RFQ award — "the" supplier price is the same row: the **latest effective price that has not expired**. Set an expiry date on a quote that is only good until a date and it drops out automatically.

When a supplier is assigned to a BOQ item:

1. The system looks for that supplier's current price for the item
2. If found, uses it; otherwise the catalogue price

Supplier prices appear in the BOQ with an "S" indicator. Awarding an RFQ records the winner's line prices here (a price equal to their current one is not duplicated).
        `,
            },
        ],
        keywords: ['supplier prices', 'pricing', 'negotiated rates', 'supplier rates'],
        route: '/suppliers',
        relatedArticles: ['suppliers.overview', 'catalogue.prices', 'boq.suppliers'],
    },
    {
        id: 'suppliers.contacts',
        title: 'Supplier Contacts',
        category: 'Suppliers',
        order: 6,
        summary: 'Managing multiple contacts for a supplier',
        content: `
Each supplier can have multiple contact persons with different roles and contact details.

**Contact Information:**

- Name and position
- Phone and mobile
- Email address
- Role/department
    `,
        expandableSections: [
            {
                title: 'Adding Contacts',
                content: `
1. Open the supplier record
2. Go to the **Contacts** tab
3. Click **Add Contact**
4. Enter contact details:
   - Name
   - Position/Role
   - Phone numbers
   - Email
5. Set as primary contact if applicable
6. Save
        `,
            },
            {
                title: 'Primary Contact',
                content: `
The primary contact is used as the default for:

- Order emails
- Payment remittances
- General communications

Only one contact can be primary at a time.
        `,
            },
        ],
        keywords: ['contacts', 'contact person', 'supplier contacts'],
        route: '/suppliers',
        relatedArticles: ['suppliers.overview', 'contacts.overview'],
    },
    {
        id: 'suppliers.onboarding',
        title: 'Supplier & Subcontractor Onboarding',
        category: 'Suppliers',
        order: 7,
        summary: 'Send a secure link so suppliers complete their own onboarding questionnaire',
        content: `
Instead of emailing PDF packs and re-keying the replies, send new suppliers and subcontractors a
secure onboarding link. They complete a guided questionnaire online — business details, licences,
insurances with certificate uploads, bank details and WHS declarations — and you review, verify
and approve it before anything changes in Costed.

**The workflow:**

1. Open the supplier → **Onboarding** tab → enter their email and **Send link**
2. The supplier completes the questionnaire (works on mobile — they can photograph certificates with the camera)
3. You're emailed when they submit; the **Review submission** button opens the supplier's Onboarding tab
4. Verify the entered details against the uploaded documents, then **Approve & update supplier**
5. Mark the bank details verified after confirming them by phone

**Nothing touches the supplier record until you approve** — and bank details always stay flagged
*Unverified* until you separately confirm them (fraud protection: always verify new bank details
by phone before the first payment).
    `,
        expandableSections: [
            {
                title: 'Sending the questionnaire',
                content: `
1. Open the supplier and go to the **Onboarding** tab
2. Check the email address (prefilled from the supplier record)
3. Choose the type:
   - **Subcontractor (full WHS pack)** — includes licences, White Cards, SWMS, SDS and the Site Safety Policy acknowledgement
   - **Supplier / merchant (no WHS)** — a slimmer version for material suppliers
4. Optionally add a personal note (included in the invite email)
5. Click **Send link**

The link needs no login and expires after 30 days (configurable). **Resend** generates a fresh
link (the old one stops working) while keeping any progress the supplier saved. Automatic
reminder emails chase incomplete questionnaires — by default 3, 7 and 14 days after sending.
        `,
            },
            {
                title: 'What the supplier fills in',
                content: `
The questionnaire steps through:

- **Business** — legal/trading name, entity type, ABN (checksum-checked), GST status, address.
  Sole traders without an ABN can tick *"I don't have an ABN"* — you'll see a warning at review
  time, because 47% no-ABN withholding applies unless they provide an ATO *Statement by a supplier*
- **Contacts** — primary/site contact and accounts contact
- **Licences** — contractor licence, trade certificates, and a White Card entry per worker (subcontractors)
- **Insurances** — Public Liability, Workers Compensation (or Personal Accident & Illness for
  sole traders without employees), Professional Indemnity if applicable — each with a
  certificate-of-currency upload
- **Payment details** — bank account for payment (flagged unverified until you confirm)
- **Safety (WHS)** — reads and acknowledges your Site Safety Policy, ticks any high-risk
  construction work (SWMS uploads required) and hazardous-substance use (SDS uploads)
- **Sign & Submit** — declarations and a typed electronic signature

Progress saves automatically, so they can return later on the same link. They can submit with
items still outstanding — the submission is flagged for you to chase the rest.
        `,
            },
            {
                title: 'Reviewing, verifying and approving',
                content: `
When a questionnaire is submitted, the Onboarding tab shows the full submission:

- **Outstanding items** are listed if they submitted incomplete (*"Submitted — N items outstanding"*)
- Click any **document to preview it** right in the window (PDFs and photos), with a download option
- Tick each row in **Verify against documents** once the entered policy numbers, sums insured,
  licence and White Card numbers match the uploaded certificates — Costed records who verified and when
- **Approving will change the supplier record** shows exactly what approval overwrites (name, ABN,
  phone, email, contact) as before → after
- **Approve & update supplier** writes the details, insurances and bank details onto the record
  (insurances appear on the Insurance tab with expiry tracking)
- **Request changes** emails the supplier your reason plus the outstanding list, with a reopened link
- If the supplier emails you a document later, file it with **"Supplier emailed a document?"** —
  the outstanding list updates immediately
        `,
            },
            {
                title: 'Configuration (Settings → Modules → Supplier Onboarding)',
                content: `
Configure the questionnaire for your business:

- **Site Safety Policy** — pick or upload the PDF subcontractors must read and acknowledge
- **Minimum Public Liability cover** — submissions below it are flagged (default $10,000,000)
- **Reminder schedule** — days after sending to chase incomplete questionnaires (default 3, 7, 14)
- **Link expiry** — how long the link stays live (default 30 days)
- **Intro message** — shown at the top of the questionnaire
- **Marketing consent** — whether to ask the optional photo/project-partner consent
        `,
            },
        ],
        keywords: [
            'onboarding', 'onboard supplier', 'onboard subcontractor', 'questionnaire', 'subbie pack',
            'induction', 'white card', 'SWMS', 'SDS', 'safety policy', 'WHS', 'insurance certificate',
            'certificate of currency', 'verify bank details', 'no ABN', 'sole trader', 'compliance',
            'send onboarding link', 'supplier setup',
        ],
        route: '/suppliers',
        relatedArticles: ['suppliers.overview', 'suppliers.add', 'suppliers.insurance', 'suppliers.banking'],
        screenshots: [
            {
                src: 'suppliers/onboarding-send-tab.png',
                alt: 'Onboarding tab with the send-questionnaire form',
                caption: 'Send the onboarding link from the supplier\'s Onboarding tab',
            },
            {
                src: 'suppliers/onboarding-wizard.png',
                alt: 'Public onboarding questionnaire, business details step',
                caption: 'The supplier completes the questionnaire via the emailed link — no login required',
            },
            {
                src: 'suppliers/onboarding-wizard-whs.png',
                alt: 'Safety (WHS) step of the questionnaire',
                caption: 'Safety (WHS) step: policy acknowledgement, high-risk work (SWMS) and hazardous substances',
            },
            {
                src: 'suppliers/onboarding-review.png',
                alt: 'Submitted questionnaire in the review panel',
                caption: 'Review the submission: signed details, uploaded documents and any outstanding items',
            },
            {
                src: 'suppliers/onboarding-verify.png',
                alt: 'Verify against documents checklist',
                caption: 'Tick each item once it matches the uploaded certificate, then approve',
            },
        ],
    },
];
